<template>
    <div class="p-8">
        <div class="flex flex-col gap-4">
            <h1 class="text-2xl font-bold mb-6">KPIs</h1>
            <label class="font-semibold">Select Date Range:</label>
            <span class="text-sm text-gray-500">All times in US Eastern Time Zone</span>
            <div class="flex gap-4">
                <input type="date" v-model="startDate" class="input input-bordered w-full max-w-xs"
                    placeholder="Start Date" />
                <input type="date" v-model="endDate" class="input input-bordered w-full max-w-xs"
                    placeholder="End Date" />
            </div>

            <button class="btn btn-primary w-full max-w-xs mt-4" :disabled="!isButtonActive"
                @click="fetchData(startDate, endDate)">
                Generate Report
            </button>
        </div>

        <hr class="my-8 border-t-2 border-gray-200" />

        <div v-if="loading" class="flex flex-col items-center mt-8">
            <span class="loading loading-spinner loading-lg"></span>
            <p class="text-xl font-semibold mt-4">Data is being retrieved...</p>
        </div>

        <div v-if="!loading && dataLoaded">
            <h1 class="text-2xl font-bold mb-6 mt-6">Users</h1>
            <div class="grid grid-cols-3 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Registrations</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.registrations) }}</p>
                </div>
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">FTPs</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.first_time_purchases) }}</p>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">SC Actives</h2>
                    <LineGraph 
                        :data="reportData.sc_actives"
                        data_name="SC Actives"
                    />
                </div>
            </div>
            <h1 class="text-2xl font-bold mb-6 mt-6">Gameplay</h1>
            <div class="grid grid-cols-3 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Handle (SC)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.casino_handle_sc) }}</p>
                </div>
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">GGR (SC)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.casino_ggr) }}</p>
                </div>
            </div>
            <h1 class="text-2xl font-bold mb-6 mt-6">Purchases & Redemptions</h1>
            <div class="grid grid-cols-3 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Purchases ($)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatCurrency(reportData.purchases_in_dollars) }}</p>
                </div>
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Purchases (#)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.purchase_count) }}</p>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Purchasers (#)</h2>
                    <LineGraph 
                        :data="reportData.purchasers"
                        data_name="Purchasers (#)"
                    />
                </div>
            </div>
            <div class="grid grid-cols-3 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Redemptions ($)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatCurrency(reportData.redemption_dollars) }}</p>
                </div>
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Redemptions (#)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatNumber(reportData.redemption_count) }}</p>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Redeemers (#)</h2>
                    <LineGraph 
                        :data="reportData.redeemers"
                        data_name="Redeemers (#)"
                    />
                </div>
            </div>
            <div class="grid grid-cols-3 gap-24 mt-8">
                <div class="card bg-base-200 shadow-lg p-12">
                    <h2 class="text-3xl font-semibold">Net revenue ($)</h2>
                    <p class="text-2xl font-bold mt-6">{{ formatCurrency(reportData.net_revenue) }}</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
import LineGraph from '@/components/LineGraph.vue';

export default {
    name: 'DetailedKeyPerformanceView',
    components: {
        LineGraph,
    },
    data() {
        return {
            startDate: '',
            endDate: '',
            loading: false,
            dataLoaded: false,
            reportData: {},
        };
    },
    computed: {
        isButtonActive() {
            if (!this.startDate || !this.endDate) return false
            const start = new Date(this.startDate)
            const end = new Date(this.endDate)
            const now = new Date()
            return start <= end && start <= now
        }
    },
    methods: {
        formatNumber(value) {
            if (value == null) return '0';
            return value.toLocaleString();
        },
        formatCurrency(value) {
            if (value == null) return '$0';
            return `$${value.toLocaleString()}`;
        },
        async fetchData(start, end) {
            this.loading = true;
            this.dataLoaded = false;

            const url = `${process.env.VUE_APP_API_GATEWAY_URL}/stats/kpi?start=${start}&end=${end}`;
            const token = this.$store.getters['access_token'];

            try {
                const response = await axios.get(url, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                    },
                });

                const data = response.data.data;
                console.log(data);

                let registrations = 0;
                let first_time_purchases = 0;
                let casino_handle_sc = 0;
                let casino_ggr = 0;
                let purchases_in_dollars = 0;
                let purchase_count = 0;
                let redemption_dollars = 0;
                let redemption_count = 0;
                let redeemers = [];
                let purchasers = [];
                let sc_actives = [];

                for (const row of data) {
                    registrations += row.registrations;
                    first_time_purchases += row.first_time_purchases;
                    casino_handle_sc += row.casino_handle_sc;
                    casino_ggr += row.casino_ggr;
                    purchases_in_dollars += row.purchases_in_dollars;
                    purchase_count += row.purchase_count;
                    redemption_dollars += row.redemption_dollars;
                    redemption_count += row.redemption_count;
                    redeemers.push({
                        date: row.day_stamp,
                        count: row.redeemers,
                    });
                    purchasers.push({
                        date: row.day_stamp,
                        count: row.purchasers,
                    });
                    sc_actives.push({
                        date: row.day_stamp,
                        count: row.sc_actives,
                    });
                }

                this.reportData = {
                    registrations: registrations,
                    first_time_purchases: first_time_purchases,
                    casino_handle_sc: casino_handle_sc,
                    casino_ggr: casino_ggr,
                    purchases_in_dollars: purchases_in_dollars,
                    purchase_count: purchase_count,
                    redemption_dollars: redemption_dollars,
                    redemption_count: redemption_count,
                    redeemers: redeemers,
                    purchasers: purchasers,
                    sc_actives: sc_actives,
                    net_revenue: purchases_in_dollars - redemption_dollars,
                };

                console.log(this.reportData.sc_actives);

                this.dataLoaded = true;
            } catch (error) {
                console.error("Error fetching KPI data:", error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
